import { Link, useLocation } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import { LeadForm } from "@/components/LeadForm";
import { Seo } from "@/components/Seo";
import { Sparkles, ShieldCheck, MessageCircle } from "lucide-react";

/**
 * ContactSales — soft-landing page for legacy `/billing` and `/pricing`
 * inbound links following the 2026-02 Stripe removal (ROADMAP §26h).
 *
 * Any cached search-engine result, external link, or bookmarked URL that
 * still points at the old billing surface lands here rather than a bare
 * 404. The page mirrors the tone of `/pilot` but is scoped to a pricing
 * conversation, converting ambient traffic into named prospects via the
 * shared `LeadForm` (kind="sales").
 */
export default function ContactSales() {
  const { pathname } = useLocation();
  // Tag the source so the Owner can distinguish legacy /billing vs /pricing
  // vs organic /contact-sales traffic in the leads panel.
  const source = pathname.startsWith("/billing")
    ? "legacy-billing-redirect"
    : pathname.startsWith("/pricing")
    ? "pricing-page"
    : "contact-sales-page";

  return (
    <PublicShell active="/contact-sales">
      <Seo
        title="Contact sales — ERIO by EKSELENS"
        path="/contact-sales"
        description="Talk to ERIO sales. Fixed-fee scopes, no self-serve billing — every engagement starts with a scoping conversation so the pilot is right-sized to your risk decisions."
      />
      <section
        className="mx-auto grid max-w-6xl gap-12 px-6 py-16 lg:grid-cols-2 lg:items-start"
        data-testid="contact-sales-page"
      >
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-ek-purple">
            Contact sales
          </span>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">
            No self-serve billing. Every scope is a real conversation.
          </h1>
          <p className="mt-4 text-base text-ek-ink/70">
            ERIO uses a fixed-fee, right-sized pricing model. We publish scope shapes rather than
            list prices so every engagement ends up matched to your risk-decision surface — not to
            an off-the-shelf seat count.
          </p>

          <div className="mt-8 space-y-5">
            <div className="flex items-start gap-3" data-testid="contact-sales-value-1">
              <Sparkles className="mt-0.5 shrink-0 text-ek-purple" size={20} />
              <div>
                <p className="text-sm font-semibold text-ek-ink">Fixed-fee scoping</p>
                <p className="mt-0.5 text-sm text-ek-ink/65">
                  A 45-minute call to map your top risk-decision workflows to a bounded pilot or
                  production scope.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="contact-sales-value-2">
              <ShieldCheck className="mt-0.5 shrink-0 text-ek-purple" size={20} />
              <div>
                <p className="text-sm font-semibold text-ek-ink">No hidden add-ons</p>
                <p className="mt-0.5 text-sm text-ek-ink/65">
                  DORA, ISO 27001, SOC 2, WCAG 2.1 AA readiness — all included. See the{" "}
                  <Link to="/trust" className="font-semibold text-ek-purple hover:underline">
                    Trust Centre
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="contact-sales-value-3">
              <MessageCircle className="mt-0.5 shrink-0 text-ek-purple" size={20} />
              <div>
                <p className="text-sm font-semibold text-ek-ink">One working day</p>
                <p className="mt-0.5 text-sm text-ek-ink/65">
                  Every enquiry gets a real reply within one working day, UK business hours.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-8 text-sm text-ek-ink/70">
            Prefer to skip pricing and go straight to a pilot?{" "}
            <Link to="/pilot" className="font-semibold text-ek-purple hover:underline">
              Scope a design-partner pilot →
            </Link>
          </p>
        </div>

        <div className="rounded-2xl border border-black/5 bg-white p-7 shadow-sm lg:p-8">
          <h2 className="text-lg font-semibold">Talk to sales</h2>
          <p className="mt-1 mb-5 text-sm text-ek-ink/70">
            Share a little context and a member of the sales team will be in touch with pricing
            tailored to your scope.
          </p>
          <LeadForm
            kind="sales"
            source={source}
            messagePlaceholder="Anything specific about your team size, connectors, or timeline?"
          />
        </div>
      </section>
    </PublicShell>
  );
}
